import { useState } from 'react'
import { useTasks } from '../context/TaskContext'
import { formatDateTime, getPriorityColor, getCategoryStyle, isOverdue, getRelativeTime } from '../utils/dateUtils'
import TaskModal from './TaskModal'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Check, 
  Edit2, 
  Trash2, 
  Calendar, 
  Clock, 
  Tag,
  AlertCircle,
  MoreVertical
} from 'lucide-react'

function TaskItem({ task }) {
  const { toggleTaskComplete, deleteTask } = useTasks()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isTaskOverdue = isOverdue(task.dueDate, task.dueTime) && task.status !== 'completed'
  const categoryStyle = getCategoryStyle(task.category)

  const handleToggle = async () => {
    await toggleTaskComplete(task.id)
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      setIsDeleting(true)
      setTimeout(async () => {
        await deleteTask(task.id)
      }, 300)
    }
  }

  const priorityColors = {
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, x: -100 }
  }

  return (
    <>
      <motion.div
        variants={itemVariants}
        initial="hidden"
        animate={isDeleting ? "exit" : "visible"}
        exit="exit"
        layout
        className={`group relative flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border transition-all duration-300 ${
          task.status === 'completed' 
            ? 'border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50' 
            : isTaskOverdue 
              ? 'border-red-200 dark:border-red-800/50 shadow-md shadow-red-500/10' 
              : 'border-gray-100 dark:border-gray-700 hover:border-indigo-200 dark:hover:border-indigo-800 hover:shadow-lg hover:shadow-indigo-500/10'
        }`}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {/* Priority indicator line */}
        <div className={`absolute left-0 top-4 bottom-4 w-1 rounded-full transition-all duration-300 ${
          task.status === 'completed' 
            ? 'bg-gray-300 dark:bg-gray-600' 
            : task.priority === 'high' 
              ? 'bg-red-500' 
              : task.priority === 'medium' 
                ? 'bg-amber-500' 
                : 'bg-emerald-500'
        }`} />

        {/* Checkbox */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleToggle}
          className={`flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
            task.status === 'completed'
              ? 'bg-emerald-500 border-emerald-500'
              : 'border-gray-300 dark:border-gray-600 hover:border-indigo-500 dark:hover:border-indigo-400'
          }`}
        >
          <AnimatePresence>
            {task.status === 'completed' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
              >
                <Check className="w-4 h-4 text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>

        {/* Task content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`font-semibold text-gray-900 dark:text-white truncate transition-all duration-200 ${
              task.status === 'completed' ? 'line-through text-gray-400 dark:text-gray-600' : ''
            }`}>
              {task.title}
            </h4>
            
            {/* Priority badge */}
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${priorityColors[task.priority]}`}>
              {task.priority}
            </span>

            {/* Category badge */}
            <span className={`text-xs px-2 py-0.5 rounded-full ${categoryStyle.bg} ${categoryStyle.color}`}>
              {categoryStyle.icon} {task.category}
            </span>

            {/* Overdue badge */}
            {isTaskOverdue && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300 border border-red-200 dark:border-red-800 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                overdue
              </span>
            )}
          </div>

          {task.description && (
            <p className={`text-sm mt-1.5 line-clamp-2 transition-all duration-200 ${
              task.status === 'completed' ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'
            }`}>
              {task.description}
            </p>
          )}

          <div className="flex items-center gap-4 mt-2.5 text-sm">
            {(task.dueDate || task.dueTime) && (
              <span className={`flex items-center gap-1.5 ${
                isTaskOverdue 
                  ? 'text-red-600 dark:text-red-400 font-medium' 
                  : task.status === 'completed'
                    ? 'text-gray-400 dark:text-gray-600'
                    : 'text-gray-500 dark:text-gray-400'
              }`}>
                <Calendar className="w-3.5 h-3.5" />
                {isTaskOverdue ? 'Overdue' : getRelativeTime(task.dueDate, task.dueTime)}
              </span>
            )}
            
            {task.durationMinutes > 0 && (
              <span className={`flex items-center gap-1.5 ${
                task.status === 'completed' ? 'text-gray-400 dark:text-gray-600' : 'text-gray-500 dark:text-gray-400'
              }`}>
                <Clock className="w-3.5 h-3.5" />
                {task.durationMinutes} min
              </span>
            )}

            {task.tags?.length > 0 && (
              <div className="flex items-center gap-1">
                {task.tags.map((tag, i) => (
                  <span key={i} className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-md flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <AnimatePresence>
          {showActions && (
            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="flex items-center gap-1"
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsEditModalOpen(true)}
                className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleDelete}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {isEditModalOpen && (
          <TaskModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            task={task}
          />
        )}
      </AnimatePresence>
    </>
  )
}

export default TaskItem
